
import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { type EnhancedResults } from './types';
import { collectFinancialData } from './dataCollectionService';
import { runAnalysis } from './analysisRunner';
import { buildEnhancedResults } from './enhancedResultsBuilder';

export function useEnhancedAIModel(onModelGenerated: () => void) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isRunning, setIsRunning] = useState(false);
  const [analysisStage, setAnalysisStage] = useState<string>('');

  const runEnhancedFinancialModel = async () => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to run the enhanced AI financial model.",
        variant: "destructive",
      });
      return;
    }

    setIsRunning(true);
    try {
      // Stage 1: Data Collection
      setAnalysisStage('Collecting financial data...');
      const collectedData = await collectFinancialData(user.id);

      // Stage 2-5: Run Analysis
      const analysisResults = await runAnalysis(collectedData, setAnalysisStage);

      // Stage 6: Generate Enhanced Results
      setAnalysisStage('Generating comprehensive report...');

      const phases = ['prospecting', 'exploration', 'development'] as const;
      
      const enhancedResults: EnhancedResults = buildEnhancedResults({
        ...analysisResults,
        hasBalanceSheetData: collectedData.hasBalanceSheetData,
        hasAssumptions: collectedData.hasAssumptions,
        phases
      });

      toast({
        title: "Enhanced AI Model Completed",
        description: `Deep learning analysis complete with ${enhancedResults.enhancedMetrics.confidenceScore}% confidence. All four scenarios (optimistic, realistic, pessimistic, worst case) now available.`,
      });

      localStorage.setItem('enhancedModelResults', JSON.stringify(enhancedResults));
      onModelGenerated();

    } catch (error: any) {
      console.error('Error running enhanced financial model:', error);
      toast({
        title: "Enhanced Model Generation Failed",
        description: error.message || "Failed to generate enhanced financial model. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRunning(false);
      setAnalysisStage('');
    }
  };

  return {
    isRunning,
    analysisStage,
    runEnhancedFinancialModel
  };
}
